import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format } from "date-fns";
import { usePartialUpdateContact, useRelationshipTypes, useDeleteContact } from "@/hooks/api/useContacts";
import { RelationshipTypeEnum, Contact } from "@/types/api";
import { useToast } from "@/hooks/use-toast";
import { DatePicker } from "@/components/onboarding/DatePicker";
import { User, Trash2 } from "lucide-react";
import { AvatarSelector } from "@/components/contacts/AvatarSelector";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { COUNTRIES } from "@/constants/countries";

interface PersonalizeContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  contact: Contact;
  onSuccess?: () => void;
  onSkip?: () => void;
  showSkipButton?: boolean;
}

const PersonalizeContactModal = ({
  isOpen,
  onClose,
  contact,
  onSuccess,
  onSkip,
  showSkipButton = false,
}: PersonalizeContactModalProps) => {
  const [name, setName] = useState(contact.name);
  const [email, setEmail] = useState(contact.email || "");
  const [phone, setPhone] = useState(contact.phone || "");
  const [country, setCountry] = useState(contact.country || "");
  const [avatar, setAvatar] = useState(contact.avatar || "");
  const [relationshipType, setRelationshipType] = useState<RelationshipTypeEnum | "">("");
  const [birthday, setBirthday] = useState<Date | undefined>(
    contact.birthday ? new Date(contact.birthday) : undefined
  );
  const [customFields, setCustomFields] = useState<Record<string, any>>({});
  const { toast } = useToast();

  const updateContact = usePartialUpdateContact(contact.id);
  const deleteContact = useDeleteContact();
  const { data: relationshipTypes, isLoading: isLoadingTypes } = useRelationshipTypes();

  // Initialize with existing contact data
  useEffect(() => {
    // Reset all fields when contact changes
    setName(contact.name);
    setEmail(contact.email || "");
    setPhone(contact.phone || "");
    setCountry(contact.country || "");
    setAvatar(contact.avatar || "");
    setBirthday(contact.birthday ? new Date(contact.birthday) : undefined);

    if (contact.relationship_type) {
      setRelationshipType(contact.relationship_type as RelationshipTypeEnum);
    } else {
      setRelationshipType("");
    }

    // Merge relationship data and custom_data
    const mergedFields: Record<string, any> = {};

    // First add relationship data if it exists
    if (contact.relationship?.data) {
      const relationshipData = contact.relationship.data;
      Object.keys(relationshipData).forEach(key => {
        // Skip 'id' and 'type' fields from relationship data
        if (key !== 'id' && key !== 'type') {
          mergedFields[key] = relationshipData[key];
        }
      });
    }

    // Then add custom_data (will override if same keys exist)
    if (contact.custom_data) {
      Object.keys(contact.custom_data).forEach(key => {
        mergedFields[key] = contact.custom_data[key];
      });
    }

    setCustomFields(mergedFields);
  }, [contact, contact.id]);

  const getDynamicFields = () => {
    if (!relationshipType || !relationshipTypes) return [];
    const typeData = relationshipTypes.find((t: any) => t.type === relationshipType);
    return typeData?.fields || [];
  };

  const formatLabel = (fieldName: string) => {
    return fieldName
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const handleSave = async () => {
    try {
      // Validate name
      if (!name.trim()) {
        toast({
          title: "Name required",
          description: "Please enter a contact name",
          variant: "destructive",
        });
        return;
      }

      // Validate required fields
      const dynamicFields = getDynamicFields();
      const requiredFields = dynamicFields.filter((field: any) => field.required);

      for (const field of requiredFields) {
        const value = customFields[field.name];
        if (!value && value !== false && value !== 0) {
          toast({
            title: "Missing required field",
            description: `Please fill in the ${field.name.replace(/_/g, " ")} field`,
            variant: "destructive",
          });
          return;
        }
      }

      // Prepare update data
      const relationshipData: Record<string, any> = {};
      const otherCustomData: Record<string, any> = {};

      const dynamicFieldNames = new Set(dynamicFields.map((field: any) => field.name));

      Object.keys(customFields).forEach((key) => {
        const value = customFields[key];
        if (value !== "" && value !== null && value !== undefined) {
          if (dynamicFieldNames.has(key)) {
            relationshipData[key] = value;
          } else {
            otherCustomData[key] = value;
          }
        }
      });

      const updateData: any = {
        name: name.trim(),
      };

      if (email) updateData.email = email.trim();
      if (phone) updateData.phone = phone.trim();
      if (country) updateData.country = country.trim();

      if (relationshipType) {
        updateData.relationship_type = relationshipType;
      }

      if (birthday) {
        updateData.birthday = format(birthday, "yyyy-MM-dd");
      }

      if (Object.keys(relationshipData).length > 0) {
        updateData.relationship_data = relationshipData;
      }

      if (Object.keys(otherCustomData).length > 0) {
        updateData.custom_data = otherCustomData;
      }

      await updateContact.mutateAsync(updateData);

      toast({
        title: "Contact updated",
        description: "Contact details have been saved successfully.",
      });

      onSuccess?.();
      onClose();
    } catch (error: any) {
      console.error("Error updating contact:", error);
      toast({
        title: "Error updating contact",
        description: error?.response?.data?.error || "Failed to update contact details.",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async () => {
    if (!confirm(`Are you sure you want to delete ${contact.name}? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteContact.mutateAsync(contact.id);
      toast({
        title: "Contact deleted",
        description: "Contact has been deleted successfully.",
      });
      onSuccess?.();
      onClose();
    } catch (error: any) {
      console.error("Error deleting contact:", error);
      toast({
        title: "Error deleting contact",
        description: error?.response?.data?.error || "Failed to delete contact.",
        variant: "destructive",
      });
    }
  };

  const handleSkip = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[621px] p-8 md:p-[56px] max-h-[90vh] overflow-y-auto">
        {/* Avatar and Name Section */}
        <div className="flex flex-col items-center mb-8">
          <h2 className="text-[16px] font-bold text-wabisabi-plum mb-4">{contact.name}</h2>
          <Avatar className="w-20 h-20 mb-4">
            {avatar ? (
              <AvatarImage src={`/images/avatars/${avatar}`} alt={contact.name} />
            ) : null}
            <AvatarFallback>
              <User className="w-10 h-10 text-gray-500" />
            </AvatarFallback>
          </Avatar>
          <AvatarSelector contact={contact} onAvatarUpdate={setAvatar} />
        </div>

        <div className="space-y-6">
          {/* Name Field */}
          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm font-medium text-gray-700">
              Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              type="text"
              placeholder="Enter contact name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full"
            />
          </div>

          {/* Email Field */}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium text-gray-700">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full"
            />
          </div>

          {/* Phone Field */}
          <div className="space-y-2">
            <Label htmlFor="phone" className="text-sm font-medium text-gray-700">
              Phone
            </Label>
            <Input
              id="phone"
              type="tel"
              placeholder="Enter phone number"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full"
            />
          </div>

          {/* Country Field */}
          <div className="space-y-2">
            <Label htmlFor="country" className="text-sm font-medium text-gray-700">
              Country
            </Label>
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select country" />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {COUNTRIES.map((countryName) => (
                  <SelectItem key={countryName} value={countryName}>
                    {countryName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Relationship Type */}
          <div className="space-y-2">
            <Label htmlFor="relationship" className="text-sm font-medium text-gray-700">
              Relationship
            </Label>
            <Select
              value={relationshipType}
              onValueChange={(value) => setRelationshipType(value as RelationshipTypeEnum)}
              disabled={isLoadingTypes}
            >
              <SelectTrigger className="w-full">
                <SelectValue
                  placeholder={isLoadingTypes ? "Loading..." : "Select relationship type"}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="mother">Mother</SelectItem>
                <SelectItem value="father">Father</SelectItem>
                <SelectItem value="sister">Sister</SelectItem>
                <SelectItem value="brother">Brother</SelectItem>
                <SelectItem value="spouse">Spouse</SelectItem>
                <SelectItem value="child">Child</SelectItem>
                <SelectItem value="friend">Friend</SelectItem>
                <SelectItem value="colleague">Colleague</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Birthday Field */}
          <div className="space-y-2">
            <Label htmlFor="birthday" className="text-sm font-medium text-gray-700">
              Birthday
            </Label>
            <DatePicker
              id="birthday"
              value={birthday}
              onChange={setBirthday}
              placeholder="Select date"
            />
          </div>

          {/* Dynamic fields based on relationship type */}
          {relationshipType && getDynamicFields().length > 0 && (
            <>
              {getDynamicFields().map((field: any) => {
                const getInputType = () => {
                  switch (field.type) {
                    case "CharField":
                    case "TextField":
                      return "text";
                    case "DateField":
                      return "date";
                    case "BooleanField":
                      return "checkbox";
                    default:
                      return "text";
                  }
                };

                const inputType = getInputType();

                if (inputType === "checkbox") {
                  return (
                    <div key={field.name} className="flex items-center space-x-2">
                      <input
                        id={field.name}
                        type="checkbox"
                        checked={customFields[field.name] || false}
                        onChange={(e) =>
                          setCustomFields({
                            ...customFields,
                            [field.name]: e.target.checked,
                          })
                        }
                        className="h-4 w-4 text-wabisabi-plum focus:ring-wabisabi-plum border-gray-300 rounded"
                      />
                      <Label htmlFor={field.name} className="text-sm font-medium text-gray-700">
                        {formatLabel(field.name)}
                      </Label>
                    </div>
                  );
                }

                if (inputType === "date") {
                  return (
                    <div key={field.name} className="space-y-2">
                      <Label htmlFor={field.name} className="text-sm font-medium text-gray-700">
                        {formatLabel(field.name)}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                      </Label>
                      <DatePicker
                        id={field.name}
                        value={
                          customFields[field.name] ? new Date(customFields[field.name]) : undefined
                        }
                        onChange={(date) => {
                          setCustomFields({
                            ...customFields,
                            [field.name]: date ? format(date, "yyyy-MM-dd") : "",
                          });
                        }}
                        placeholder={
                          field.help_text || `Select ${formatLabel(field.name).toLowerCase()}`
                        }
                      />
                    </div>
                  );
                }

                return (
                  <div key={field.name} className="space-y-2">
                    <Label htmlFor={field.name} className="text-sm font-medium text-gray-700">
                      {formatLabel(field.name)}
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                    </Label>
                    <Input
                      id={field.name}
                      type={inputType}
                      placeholder={
                        field.help_text || `Enter ${formatLabel(field.name).toLowerCase()}`
                      }
                      value={customFields[field.name] || ""}
                      onChange={(e) =>
                        setCustomFields({
                          ...customFields,
                          [field.name]: e.target.value,
                        })
                      }
                      required={field.required}
                      className="w-full"
                    />
                  </div>
                );
              })}
            </>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col gap-3 !mt-8">
            <Button
              onClick={handleSave}
              disabled={updateContact.isPending}
              className="w-full bg-wabisabi-plum hover:bg-wabisabi-plum/90 text-white font-bold text-[14px] font-modernist py-[20px]"
            >
              {updateContact.isPending ? "Saving..." : "Save"}
            </Button>

            {showSkipButton && onSkip && (
              <Button
                variant="outline"
                onClick={onSkip}
                className="w-full font-bold text-[14px] font-modernist py-[20px] border-wabisabi-plum text-wabisabi-plum hover:bg-wabisabi-plum/5"
              >
                Skip
              </Button>
            )}

            {!showSkipButton && (
              <Button
                variant="outline"
                onClick={handleDelete}
                disabled={deleteContact.isPending}
                className="w-full font-bold text-[14px] font-modernist py-[20px] border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                {deleteContact.isPending ? "Deleting..." : "Delete"}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PersonalizeContactModal;
